import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { Routes, Route } from "react-router-dom"
// Import Routes all
import { authProtectedRoutes, publicRoutes } from "./routes"

// Import all middleware
import Authmiddleware from "./routes/route"

// layouts Format
import HorizontalLayout from "./components/HorizontalLayout/"
import NonAuthLayout from "./components/NonAuthLayout"

// Import scss
import "./assets/scss/theme.scss"
import "./assets/css/common.css"
import { socket } from "helpers/Connection"
import { PermissionContext } from "AppContext"
import secureLocalStorage from "react-secure-storage"
import { app_url } from "app_config"
import { useDispatch } from "react-redux"
import { startTimer, stopTimer } from "store/timer/actions"

const App = () => {
  const Layout = HorizontalLayout

  const [notifications, setNotifications] = useState([])
  const dispatch = useDispatch()

  // notification web socket
  useEffect(() => {
    const requestNotificationPermission = async () => {
      try {
        // Check if the browser supports notifications
        if (!("Notification" in window)) {
          console.log("This browser does not support desktop notifications")
          return
        }

        // Request permission if not already granted
        if (Notification.permission !== "granted") {
          const permission = await Notification.requestPermission()
          if (permission !== "granted") {
            console.log("Notification Permission Denied by User")
            return
          }
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error)
      }
    }

    const handleMeetingNotification = data => {
      try {
        if (Notification.permission === "granted") {
          if (data.type === "MOM_UPDATED") {
            const notification = new Notification(
              `${data.sender.name} has updated MOM details, please do check.`,
              {
                body: `MOM : ${data.mom.title}`,
                icon: "/brandLogo.png",
                tag: `mom-update-${data.mom.id}`, // Prevents duplicate notifications
                requireInteraction: false, // Notification remains until user interacts
                silent: false,
                vibrate: [200, 100, 200], // Vibration pattern for mobile
              }
            )
            notification.onclick = function () {
              window.open(`${app_url}minutes-of-meeting`, "_blank")
            }

            setNotifications(prev => [
              {
                title: `${data.sender.name} has updated MOM details, please do check.`,
                createdAt: data?.timestamp,
              },
              ...prev,
            ])
          } else {
            const notification = new Notification(
              `${data.sender.name} has created MOM for meeting - ${data.mom.title}`,
              {
                body: `MOM : ${data.mom.title}`,
                icon: "/brandLogo.png",
                tag: `mom-${data.mom.id}`, // Prevents duplicate notifications
                requireInteraction: false, // Notification remains until user interacts
                silent: false,
                vibrate: [200, 100, 200], // Vibration pattern for mobile
              }
            )
            notification.onclick = function () {
              window.open(`${app_url}minutes-of-meeting`, "_blank")
            }

            setNotifications(prev => [
              {
                title: `${data.sender.name} has created MOM for meeting - ${data.mom.title}`,
                createdAt: data?.timestamp,
              },
              ...prev,
            ])
          }
        }
      } catch (error) {
        console.error("Error showing notification:", error)
      }
    }

    const handleTaskNotification = data => {
      try {
        if (Notification.permission === "granted") {
          const notification = new Notification(
            `${data.sender.name} has assigned you a task - ${data.task.title}`,
            {
              body: `Task Name : ${data.task.title}`,
              icon: "/brandLogo.png",
              tag: `task-${data.task.id}`, // Prevents duplicate notifications
              requireInteraction: false, // Notification remains until user interacts
              silent: false,
              vibrate: [200, 100, 200], // Vibration pattern for mobile
            }
          )
          notification.onclick = function () {
            window.open(`${app_url}tasks`, "_blank")
          }
        }
        setNotifications(prev => [
          {
            title: `${data.sender.name} has assigned you a task - ${data.task.title}`,
            createdAt: data?.timestamp,
          },
          ...prev,
        ])
      } catch (error) {
        console.error("Error showing notification:", error)
      }
    }

    const handleAutoTaskNotification = data => {
      try {
        if (Notification.permission === "granted") {
          const notification = new Notification(
            `New Task Assigned - ${data?.task?.title}`,
            {
              body: `Task Name : ${data?.task?.title}`,
              icon: "/brandLogo.png",
              tag: `task-${data?.task?.id}`, // Prevents duplicate notifications
              requireInteraction: false, // Notification remains until user interacts
              silent: false,
              vibrate: [200, 100, 200], // Vibration pattern for mobile
            }
          )
          notification.onclick = function () {
            window.open(`${app_url}tasks`, "_blank")
          }
        }

        setNotifications(prev => [
          {
            title: `New Task Assigned - ${data?.task?.title}`,
            createdAt: data?.timestamp,
          },
          ...prev,
        ])
      } catch (error) {
        console.error("Error showing notification:", error)
      }
    }

    const handleClientAssignmentNotification = data => {
      try {
        if (Notification.permission === "granted") {
          const notification = new Notification(
            `${data.sender.name} has assigned you a new client.`,
            {
              body: `Client Name: ${data.client.clientName}`,
              icon: "/brandLogo.png",
              tag: `client-${data.client.id}`, // Prevents duplicate notifications
              requireInteraction: false, // Notification remains until user interacts
              silent: false,
              vibrate: [200, 100, 200], // Vibration pattern for mobile
            }
          )
          notification.onclick = function () {
            window.open(`${app_url}clients`, "_blank")
          }
        }

        setNotifications(prev => [
          {
            title: `New Client Assigned - ${data?.client?.clientName}`,
            createdAt: data?.timestamp,
          },
          ...prev,
        ])
      } catch (error) {
        console.error("Error showing client assignment notification:", error)
      }
    }

    const handleTimerStopEvent = data => {
      try {
        if (!data.timer) {
          dispatch(stopTimer())
        }
      } catch (error) {
        console.error("Error stopping timer through socket connection:", error)
      }
    }

    const handleTimerStartEvent = data => {
      try {
        if (data.timer) {
          dispatch(startTimer())
        }
      } catch (error) {
        console.error("Error starting timer through socket connection:", error)
      }
    }

    requestNotificationPermission()

    // Listen for task notifications
    socket.on("meetingNotification", handleMeetingNotification)
    socket.on("taskNotification", handleTaskNotification)
    socket.on("autoTaskNotification", handleAutoTaskNotification)
    socket.on(
      "assignedUserNotificationInClient",
      handleClientAssignmentNotification
    )
    socket.on("timerStopped", handleTimerStopEvent)
    socket.on("timerStarted", handleTimerStartEvent)

    // Cleanup
    return () => {
      socket.off("meetingNotification")
      socket.off("taskNotification")
      socket.off("autoTaskNotification")
      socket.off("assignedUserNotificationInClient")
      socket.off("timerStopped")
      socket.off("timerStarted")
    }
  }, [])

  // =======================

  useEffect(() => {
    if (secureLocalStorage.getItem("authUser") == null) {
      secureLocalStorage.removeItem("authUser")
      secureLocalStorage.removeItem("token")
    }
  }, [])

  return (
    <React.Fragment>
      <PermissionContext>
        <Routes>
          {publicRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={<NonAuthLayout>{<route.component />}</NonAuthLayout>}
              key={idx}
              exact={true}
            />
          ))}

          {authProtectedRoutes.map((route, idx) => (
            <Route
              path={route.path}
              element={
                <Authmiddleware>
                  <Layout
                    notifications={notifications}
                    setNotifications={setNotifications}
                  >
                    {route.component}
                  </Layout>
                </Authmiddleware>
              }
              key={idx}
              exact={true}
            />
          ))}
        </Routes>
      </PermissionContext>
    </React.Fragment>
  )
}

App.propTypes = {
  layout: PropTypes.any,
}

export default App

// {title === "UTR Number" ? (
//             <div className="col-12 mb-4">
//               <div className="row">
//                 <div className={`col-md-${"5"} mb-md-1 mb-3`}>
//                   <label>
//                     Did we apply, or ask for the reference to be resent?{" "}
//                     <span className="text-danger">*</span>
//                   </label>
//                   <select
//                     className="form-select input-field"
//                     value={data?.isUTRApplied}
//                     disabled={isReadOnly}
//                     name="isUTRApplied"
//                     onChange={handleSetData}
//                   >
//                     <option value="">Select </option>
//                     <option value="yes">Yes</option>
//                     <option value="no">No</option>
//                   </select>
//                   {/* <span className="error">{statusValidate}</span> */}
//                 </div>
//                 {data?.isUTRApplied === "yes" && (
//                   <div className="col-md-4">
//                     <label htmlFor="#">
//                       What date did we apply?{" "}
//                       <span className="text-danger">*</span>
//                     </label>
//                     <input
//                       type="date"
//                       className="form-control input-field"
//                       value={data?.UTRAppliedDate}
//                       disabled={isReadOnly}
//                       name="UTRAppliedDate"
//                       onChange={handleSetData}
//                     />
//                   </div>
//                 )}
//               </div>
//             </div>
//           ) : null}

//           {title === "CAC Number" ? (
//             <div className="col-12 mb-4">
//               <div className="row">
//                 <div className={`col-md-${"5"} mb-md-1 mb-3`}>
//                   <label>
//                     Did we apply, or ask for the reference to be resent?{" "}
//                     <span className="text-danger">*</span>
//                   </label>
//                   <select
//                     className="form-select input-field"
//                     value={data?.isCACApplied}
//                     disabled={isReadOnly}
//                     name="isCACApplied"
//                     onChange={handleSetData}
//                   >
//                     <option value="">Select </option>
//                     <option value="yes">Yes</option>
//                     <option value="no">No</option>
//                   </select>
//                   {/* <span className="error">{statusValidate}</span> */}
//                 </div>
//                 {data?.isCACApplied === "yes" && (
//                   <div className="col-md-4">
//                     <label htmlFor="#">
//                       What date did we apply?{" "}
//                       <span className="text-danger">*</span>
//                     </label>
//                     <input
//                       type="date"
//                       className="form-control input-field"
//                       value={data?.CACAppliedDate}
//                       disabled={isReadOnly}
//                       name="CACAppliedDate"
//                       onChange={handleSetData}
//                     />
//                   </div>
//                 )}
//               </div>
//             </div>
//           ) : null}

//           {title === "PAYE Number" ? (
//             <div className="col-12 mb-4">
//               <div className="row">
//                 <div className={`col-md-${"5"} mb-md-1 mb-3`}>
//                   <label>
//                     Did we apply, or ask for the reference to be resent?{" "}
//                     <span className="text-danger">*</span>
//                   </label>
//                   <select
//                     className="form-select input-field"
//                     value={data?.isPAYEApplied}
//                     disabled={isReadOnly}
//                     name="isPAYEApplied"
//                     onChange={handleSetData}
//                   >
//                     <option value="">Select </option>
//                     <option value="yes">Yes</option>
//                     <option value="no">No</option>
//                   </select>
//                   {/* <span className="error">{statusValidate}</span> */}
//                 </div>
//                 {data?.isPAYEApplied === "yes" && (
//                   <div className="col-md-4">
//                     <label htmlFor="#">
//                       What date did we apply?{" "}
//                       <span className="text-danger">*</span>
//                     </label>
//                     <input
//                       type="date"
//                       className="form-control input-field"
//                       value={data?.PAYEAppliedDate}
//                       disabled={isReadOnly}
//                       name="PAYEAppliedDate"
//                       onChange={handleSetData}
//                     />
//                   </div>
//                 )}
//               </div>
//             </div>
//           ) : null}

//           {title === "VAT Number" ? (
//             <div className="col-12 mb-4">
//               <div className="row">
//                 <div className={`col-md-${"5"} mb-md-1 mb-3`}>
//                   <label>
//                     Did we apply, or ask for the reference to be resent?{" "}
//                     <span className="text-danger">*</span>
//                   </label>
//                   <select
//                     className="form-select input-field"
//                     value={data?.isVATApplied}
//                     disabled={isReadOnly}
//                     name="isVATApplied"
//                     onChange={handleSetData}
//                   >
//                     <option value="">Select </option>
//                     <option value="yes">Yes</option>
//                     <option value="no">No</option>
//                   </select>
//                   {/* <span className="error">{statusValidate}</span> */}
//                 </div>
//                 {data?.isVATApplied === "yes" && (
//                   <div className="col-md-4">
//                     <label htmlFor="#">
//                       What date did we apply?{" "}
//                       <span className="text-danger">*</span>
//                     </label>
//                     <input
//                       type="date"
//                       className="form-control input-field"
//                       value={data?.VATAppliedDate}
//                       disabled={isReadOnly}
//                       name="VATAppliedDate"
//                       onChange={handleSetData}
//                     />
//                   </div>
//                 )}
//               </div>
//             </div>
//           ) : null}
